import { useEffect, useState } from 'react';
import { signIn, confirmSignIn } from 'aws-amplify/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAuth } from '../../core/authContext';
import { returnPathFrom } from './returnPath';
import { NEW_PASSWORD_MIN_LENGTH, validateNewPassword } from './validateNewPassword';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export function LoginPage() {
  const mainAppUrl = import.meta.env.REACT_APP_MAIN_APP_URL ?? '';
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = returnPathFrom(location.state);
  const { isInitializing, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  useEffect(() => {
    if (!isInitializing && isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isInitializing, isAuthenticated, navigate, returnTo]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email.trim().toLowerCase(),
        password,
      });
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
        return;
      }
      if (isSignedIn) {
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const validationError = validateNewPassword(newPassword, confirmNewPassword);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!needsNewPassword) return;
    setLoading(true);
    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse: newPassword });
      if (isSignedIn) {
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set new password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: HEIGHT_BELOW_BARS, p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 420 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
          Flashlight Learning
        </Typography>
        {needsNewPassword ? (
          <form onSubmit={handleCompleteNewPassword} noValidate>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: '#5B5B73' }}>
                Set a new password to finish signing in.
              </Typography>
              <TextField
                label="New password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
                helperText={`At least ${NEW_PASSWORD_MIN_LENGTH} characters.`}
              />
              <TextField
                label="Confirm new password"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                fullWidth
                autoComplete="new-password"
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" size="large" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Set password and continue'}
              </Button>
            </Stack>
          </form>
        ) : (
          <form onSubmit={handleSignIn} noValidate>
            <Stack spacing={2}>
              {mainAppUrl && (
                <Typography variant="body2" sx={{ color: '#5B5B73' }}>
                  Signed in to <a href={mainAppUrl}>Flashlight360</a>? Go back there and open Reports again to continue
                  without a password.
                </Typography>
              )}
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="username"
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="current-password"
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" size="large" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Log in'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2">
                  Forgot your password? Reset it in the{' '}
                  <a href={`${mainAppUrl}/forgot-password`}>main app</a>.
                </Typography>
              </Box>
            </Stack>
          </form>
        )}
      </Paper>
    </Box>
  );
}
